import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react';

import type { FeatureFlagKeys, FeatureFlagVariant } from '../types';

export function useGetFeatureFlag<T extends FeatureFlagKeys>(featureFlag: T): FeatureFlagVariant | undefined {
  const variant = useFeatureFlagVariantKey(featureFlag) as FeatureFlagVariant | undefined;
  return variant;
}

export function useGetEnabledFeatureFlag<T extends FeatureFlagKeys>(featureFlag: T) {
  const enabled = useFeatureFlagEnabled(featureFlag);
  return Boolean(enabled);
}

/**
 * Kill-switch semantics: ONLY an explicit `false` disables. An unresolved flag reads as ENABLED.
 *
 * `useGetEnabledFeatureFlag` coerces `undefined` to `false`, which is correct for gating a new feature
 * (default to withholding) and wrong for a switch whose only job is to turn something OFF in a hurry.
 * `useFeatureFlagEnabled` returns `undefined` both while PostHog is still loading and when the key is
 * absent from the project, so failing closed would mean a slow response, a network blip, or a flag that
 * was never created in one environment silently withdraws a shipped surface. Nothing would look broken;
 * the surface would just quietly stop appearing.
 *
 * Use this only where the safe default is ON.
 */
export function useGetFeatureFlagKillSwitch<T extends FeatureFlagKeys>(featureFlag: T): boolean {
  return useFeatureFlagEnabled(featureFlag) !== false;
}
